import React, { useEffect, useState } from 'react'
import { BrowserRouter, Link, NavLink, Redirect, Route, Switch } from 'react-router-dom'
import { connect, ConnectedProps } from 'react-redux'
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import 'bootstrap/dist/css/bootstrap.min.css'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBars, faList, faQuestionCircle, faTimes, faUserCircle
} from '@fortawesome/free-solid-svg-icons';

import { RootState, userConnector } from '_store'
import { beginSession } from '_store/system/thunks'
import { loadIcons } from '_utils/icons'
import { restoreSession } from '_utils/login'

import PrivateRoute from '_components/PrivateRoute'
import { NotificationList } from '_components'

import { Login, ResetPassword, Logout } from 'Login'
import Browse from 'Browse'
import { EditItem } from 'Edit'
import { Account } from 'Account/Account';
import { Help } from 'Help/Help';

import './App.sass'

import sol_atelier_logo from './sol_atelier_logo.svg'
import { User } from '_types/system';

loadIcons()

const MenuEntry = ({ to, label, icon }) => <NavLink
    to={to}
    className="MenuEntry"
    activeClassName="active"
    component={(props) =>
        <div className={props.className} onClick={props.navigate}>
            <FontAwesomeIcon icon={icon} />
            &ensp;
            {label}
        </div>
    }
/>

type MenuProps = { user: User; onClick?: () => void; }

const Menu: React.FC<MenuProps> = ({ user, onClick }) => <div className="Menu" onClick={onClick}>
    {user && <div className="manufacturer">
        {user.manufacturer.logoUrl && <Link to="/account" component={({ navigate }) => <img src={user.manufacturer.logoUrl} alt="" onClick={navigate} />} />}
        <Link to="/account" component={({ navigate }) => <span onClick={navigate}>{user.manufacturer.brandName}</span>} />
    </div>}
    <MenuEntry to="/browse" label="Instruments" icon={faList} />
    <MenuEntry to="/account" label="Account" icon={faUserCircle} />
    <MenuEntry to="/help" label="Help" icon={faQuestionCircle} />
</div>

const PHeader: React.FC<ConnectedProps<typeof userConnector>> = ({ user }) => {
    const [open, setOpen] = useState(false)

    if (user) {
        return <>
            <div className={'backdrop' + (open ? '' : ' hidden')} onClick={() => setOpen(false)} />
            <div className={'overlay' + (open ? '' : ' hidden')}>
                <FontAwesomeIcon icon={faTimes} onClick={() => setOpen(false)} />
                <Menu user={user} onClick={() => setOpen(false)} />
            </div>
            <div className="Header">
                <div className="menu">
                    <FontAwesomeIcon icon={faBars} onClick={() => setOpen(true)} />
                    <Link to="/" className="home-link">
                        <img className="logo" src={sol_atelier_logo} alt="" />
                    </Link>
                </div>
                <div className="user">
                    <span className="name">{user.staffName || ''}</span>
                    <span><Link to="/logout">sign out</Link></span>
                </div>
            </div>
        </>
    } else {
        return <div className="Header">
            <div className="menu">
                <Link to="/" className="home-link">
                    <img className="logo" src={sol_atelier_logo} alt="" />
                </Link>
            </div>
        </div>
    }
}

const Header = userConnector(PHeader)

const appConnector = connect(
    (state: RootState) => ({ user: state.system.user }),
    { beginSession },
);

const PApp: React.FC<ConnectedProps<typeof appConnector>> = ({ beginSession, user }) => {
    useEffect(() => {
        // Check if the user is already logged in.
        const user = restoreSession()
        // If so, begin session, but don't store in local storage (it's already there).
        if (user) beginSession(user, false);
    }, [beginSession]);

    return <BrowserRouter>
        <DndProvider backend={HTML5Backend}>
            <div className="App">
                <Header />
                <div className="App-below-header">
                    {(user && <div className="App-sidebar">
                        <Menu user={user} />
                    </div>) || ''}
                    <div className="App-main">
                        <NotificationList />
                        <Switch>
                            <Route exact path="/login" component={Login} />
                            <Route exact path="/reset-password" component={ResetPassword} />
                            <Route exact path="/logout" component={Logout} />
                            <PrivateRoute exact path="/browse" component={Browse} />
                            <PrivateRoute path="/edit/:uuid" component={EditItem} />
                            <PrivateRoute path="/account" component={Account} />
                            <PrivateRoute path="/help" component={Help} />
                            <Redirect from="/" to="/browse" />
                        </Switch>
                    </div>
                </div>
            </div>
        </DndProvider>
    </BrowserRouter>
}

export const App = appConnector(PApp)
